import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { ApiError } from '@/lib/api/errors'
import { createManualReservation } from '@/lib/venue/reservationApi'
import type { ReservationDetailView, ReservationManualCreateInput } from '@/lib/venue/reservationTypes'

import { reservationDetailQueryKey } from './useVenueReservationDetail'

/**
 * 전화·현장 예약 등록 (#675 §2-2).
 *
 * 🚨 신규 예약이라 `rescheduleReservation`처럼 "이 예약"의 기존 캐시가 없다 — 대신 캘린더·현황판·목록
 * 쿼리를 무효화해 재조회를 트리거한다(요구사항 "등록 성공 시 캘린더·코트별 현황을 갱신"). 쿼리 키가
 * 접두사 배열(`['venue', venueId, 'reservation-calendar', ...]`)이라 이 3개 키만으로 날짜·코트 조합
 * 전체가 함께 무효화된다.
 */
export function useCreateManualReservation(venueId: string) {
  const queryClient = useQueryClient()

  return useMutation<ReservationDetailView, ApiError, ReservationManualCreateInput>({
    mutationFn: (input) => createManualReservation(venueId, input),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['venue', venueId, 'reservation-calendar'] })
      void queryClient.invalidateQueries({ queryKey: ['venue', venueId, 'reservation-board'] })
      void queryClient.invalidateQueries({ queryKey: ['venue', venueId, 'reservations'] })
      // 등록 직후 상세로 이동하는 경우를 대비해 캐시를 미리 채워 둔다(선택적 이득, 실패해도 무해).
      queryClient.setQueryData(reservationDetailQueryKey(venueId, data.reservation.id), data)
    },
  })
}
